import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import {
  ItemDisplay,
  LeverageCard,
  LeverageCardRow,
  PercentagePicker,
} from './leverageComponents';
import { LeverageCardProps } from '../leverageStep';
import { GroundUpLoanEconomicsStore } from '../../../stores/loanEconomics/groundUpLoanEconomicsStore';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { PercentageField } from '@roc/ui';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LTC_CATCHUP_PERCENTAGE } from '../../../utils/utils';
import { MAX_FINANCED_IR_PERCENTAGE } from '../../../utils/constants';
import { useEffect } from 'react';

const useGroundUpLeverageCardStyles = makeStyles(theme => ({
  initialAmountSection: {
    borderRadius: '16px',
    padding: theme.spacing(1, 0),
  },
}));

export const GroundUpLeverageCard = observer(
  ({
    store,
    disabled,
    instaQuote = false,
    showEmptyValues = false,
    LeverageMatrixCard,
    quoteTypeStore,
    manualQuoteSubTitle,
    onPercentageChange,
  }: LeverageCardProps) => {
    const classes = useGroundUpLeverageCardStyles();
    const calculated = store.calculatedFields;
    const ranges = store.ranges as any;
    const { loanEconomicsStore } = store;
    const interestReserveFinanced = loanEconomicsStore.interestReserveFinanced;
    const initialAmountBackground = !interestReserveFinanced
      ? 'transparent'
      : instaQuote
      ? 'rgba(5, 223, 114, 0.05)'
      : 'rgba(147, 148, 148, 0.15)';

    const totalLoanAmountGreaterThan3M = () => {
      if (loanEconomicsStore.enableManualQuote) {
        loanEconomicsStore.setIsTotalLoanAmountGreaterThan3M(
          calculated.totalLoanAmount > 3000000
        );
      } else {
        loanEconomicsStore.setIsTotalLoanAmountGreaterThan3M(
          calculated.totalLoanAmount > 3000000
        );
      }
    };

    useEffect(() => {
      if (onPercentageChange) {
        onPercentageChange();
      }
    }, [
      store.form.fields.loanToCost.value,
      store.form.fields.constructionReserve.value,
    ]);

    useEffect(() => {
      if (interestReserveFinanced && ranges?.maxLoanToCost != null) {
        const maxBaseLoanToCost =
          ranges.maxLoanToCost - MAX_FINANCED_IR_PERCENTAGE;
        const financedIrPercentage =
          store.form.fields.financedIrPercentage.value || 0;
        const baseLoanToCost =
          store.form.fields.loanToCost.value - financedIrPercentage;
        if (baseLoanToCost > maxBaseLoanToCost) {
          store.onFieldChange(
            'loanToCost',
            maxBaseLoanToCost + financedIrPercentage
          );
        }
      }
    }, [
      interestReserveFinanced,
      ranges?.maxLoanToCost,
      store.form.fields.loanToCost.value,
      store.form.fields.financedIrPercentage.value,
    ]);

    const handleLoanToCost = value => {
      store.onFieldChange('loanToCost', value);
      totalLoanAmountGreaterThan3M();
    };

    const handleConstructionReserve = value => {
      store.onFieldChange('constructionReserve', value);
      totalLoanAmountGreaterThan3M();
    };

    const handleBaseLoanToCost = value => {
      const financedIrPercentage =
        store.form.fields.financedIrPercentage.value || 0;
      store.onFieldChange('loanToCost', value + financedIrPercentage);
      totalLoanAmountGreaterThan3M();
    };

    const purchasePriceLabel = quoteTypeStore.isMidConstructionRefinance()
      ? 'Cost Spent to Date'
      : 'Purchase Price';

    return (
      <LeverageCard
        title={instaQuote ? 'Insta-Leverage Quote' : 'Request Leverage'}
        disabled={disabled}
        instaQuote={instaQuote}
        LeverageMatrixCard={LeverageMatrixCard}
        quoteTypeStore={quoteTypeStore}
        disabledWarning={showEmptyValues}
        subtitle={manualQuoteSubTitle}
      >
        <Grid item xs={12}>
          <Box
            className={classes.initialAmountSection}
            style={{ backgroundColor: initialAmountBackground }}
          >
            <Grid container spacing={2}>
              {interestReserveFinanced ? (
                <>
                  <LeverageCardRow
                    dividend={
                      <ItemDisplay
                        label="Base Amount"
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.baseAmount)
                        }
                        instaQuote={instaQuote}
                      />
                    }
                    divisor={
                      <ItemDisplay
                        label={purchasePriceLabel}
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.totalPurchasePrice)
                        }
                      />
                    }
                    result={
                      <PercentagePicker
                        fieldName={'baseLoanToCostPercentage'}
                        label="Base LTC"
                        disabled={disabled}
                        instaQuote={instaQuote}
                        value={showEmptyValues ? 0 : calculated.baseLoanToCost}
                        min={ranges?.minLoanToCost}
                        max={
                          (ranges?.maxLoanToCost ?? 100) -
                          MAX_FINANCED_IR_PERCENTAGE
                        }
                        onChange={value => handleBaseLoanToCost(value)}
                      />
                    }
                  />
                  <LeverageCardRow
                    dividend={
                      <ItemDisplay
                        label="Financed Amount"
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.financedIrAmount)
                        }
                        instaQuote={instaQuote}
                      />
                    }
                    divisor={
                      <ItemDisplay
                        label={purchasePriceLabel}
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.totalPurchasePrice)
                        }
                      />
                    }
                    result={
                      <ItemDisplay
                        label="Financed IR %"
                        value={
                          showEmptyValues
                            ? '-'
                            : formatPercentage(
                                store.form.fields.financedIrPercentage.value
                              )
                        }
                        instaQuote={instaQuote}
                      />
                    }
                  />
                  <LeverageCardRow
                    dividend={
                      <ItemDisplay
                        label={
                          instaQuote ? 'Initial Loan Amount' : 'Initial Advance'
                        }
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.initialAdvance)
                        }
                        instaQuote={instaQuote}
                      />
                    }
                    divisor={
                      <ItemDisplay
                        label={purchasePriceLabel}
                        value={
                          showEmptyValues
                            ? '-'
                            : formatCurrency(calculated.totalPurchasePrice)
                        }
                      />
                    }
                    result={
                      <ItemDisplay
                        label="LTC"
                        value={
                          showEmptyValues
                            ? '-'
                            : formatPercentage(calculated.loanToCost)
                        }
                        instaQuote={instaQuote}
                      />
                    }
                  />
                </>
              ) : (
                <LeverageCardRow
                  dividend={
                    <ItemDisplay
                      label={
                        instaQuote ? 'Initial Loan Amount' : 'Initial Advance'
                      }
                      value={
                        showEmptyValues
                          ? '-'
                          : formatCurrency(calculated.initialAdvance)
                      }
                      instaQuote={instaQuote}
                    />
                  }
                  divisor={
                    <ItemDisplay
                      label={purchasePriceLabel}
                      value={
                        showEmptyValues
                          ? '-'
                          : formatCurrency(calculated.totalPurchasePrice)
                      }
                    />
                  }
                  result={
                    <PercentagePicker
                      fieldName={'loanToCostPercentage'}
                      label="Loan to Cost"
                      disabled={disabled}
                      instaQuote={instaQuote}
                      value={
                        showEmptyValues ? 0 : store.form.fields.loanToCost.value
                      }
                      min={ranges?.minLoanToCost}
                      max={ranges?.maxLoanToCost}
                      onChange={value => handleLoanToCost(value)}
                    />
                  }
                />
              )}
            </Grid>
          </Box>
        </Grid>
        <LeverageCardRow
          dividend={
            <ItemDisplay
              label="Construction Holdback"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.constructionHoldback)
              }
              instaQuote={instaQuote}
            />
          }
          divisor={
            <ItemDisplay
              label="Proposed Construction Budget"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalProposedConstructionBudget)
              }
            />
          }
          result={
            <PercentagePicker
              label="Construction Reserve"
              fieldName="constructionReserve"
              disabled={disabled}
              instaQuote={instaQuote}
              value={
                showEmptyValues
                  ? 0
                  : store.form.fields.constructionReserve.value
              }
              min={ranges?.minConstructionReserve}
              max={ranges?.maxConstructionReserve}
              onChange={value => handleConstructionReserve(value)}
            />
          }
        />
        {calculated?.ltcCatchupDraw && (
          <LeverageCardRow
            dividend={
              <ItemDisplay
                label="LTC Catchup Draw Amount"
                value={
                  showEmptyValues
                    ? '-'
                    : formatCurrency(calculated?.ltcCatchupDrawAmount)
                }
                instaQuote={instaQuote}
              />
            }
            divisor={
              <ItemDisplay
                label="Purchase Price"
                value={
                  showEmptyValues
                    ? '-'
                    : formatCurrency(calculated?.totalPurchasePrice)
                }
              />
            }
            result={
              <ItemDisplay
                label="LTC Catchup Draw"
                value={
                  showEmptyValues
                    ? '-'
                    : formatPercentage(
                        calculated?.ltcCatchupDrawAmountPercentage ||
                          LTC_CATCHUP_PERCENTAGE
                      )
                }
                instaQuote={instaQuote}
              />
            }
          />
        )}
        <LeverageCardRow
          dividend={
            <ItemDisplay
              label="Total Loan Amount"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalLoanAmount)
              }
              instaQuote={instaQuote}
            />
          }
          divisor={
            <ItemDisplay
              label="Total Cost"
              value={
                showEmptyValues ? '-' : formatCurrency(calculated.costBasis)
              }
            />
          }
          result={
            <ItemDisplay
              label="Total Loan To Cost"
              value={
                showEmptyValues
                  ? '-'
                  : formatPercentage(calculated.calculatedLTFC)
              }
              instaQuote={instaQuote}
            />
          }
        />
        <LeverageCardRow
          dividend={
            <ItemDisplay
              label="Total Loan Amount"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalLoanAmount)
              }
              instaQuote={instaQuote}
            />
          }
          divisor={
            <ItemDisplay
              label="After Repair Value"
              value={
                showEmptyValues
                  ? '-'
                  : formatCurrency(calculated.totalAfterRepairValue)
              }
            />
          }
          result={
            <ItemDisplay
              label="ARLTV"
              value={
                showEmptyValues
                  ? '-'
                  : formatPercentage(calculated.afterRepairLTV)
              }
              instaQuote={instaQuote}
            />
          }
        />
      </LeverageCard>
    );
  }
);

const useFinancedInterestReserveStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  container: {
    borderRadius: '24px',
    border: '1px solid #E5E7EB',
    width: '100%',
    padding: '21px',
    marginLeft: '40px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: '32px',
  },
  radioGroup: {
    width: '100%',
    justifyContent: 'space-around',
  },
  percentageField: {
    width: '100px',
    marginTop: '8px',
  },
}));

export const FinancedInterestReserve = observer(
  ({ store }: { store: QuoteTypeStore }) => {
    const classes = useFinancedInterestReserveStyles();
    const loanEconomicsStore = store.loanEconomicsStore as GroundUpLoanEconomicsStore;
    const value = loanEconomicsStore.interestReserveFinanced;
    const financedIrPercentage =
      loanEconomicsStore.instaLeverageQuoteFormStore.form.fields
        .financedIrPercentage.value;
    return (
      <Box className={classes.wrapper}>
        <Box className={classes.container}>
          <FormControl component="fieldset">
            <FormLabel focused={false}>
              Add financed interest reserve?
            </FormLabel>
            <RadioGroup
              row
              className={classes.radioGroup}
              name={'addFinancedInterestReserve'}
              value={value}
              onChange={e => {
                const value = e.target.value === 'true';
                loanEconomicsStore.setAddFinancedInterestReserve(value);
                console.log({ loanEconomicsStore: loanEconomicsStore });
                loanEconomicsStore.caclulateFields(
                  store.propertiesStore.getProperties(),
                  loanEconomicsStore.instaLeverageQuoteFormStore.getFormValues()
                );
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Yes"
                id="Yes"
                checked={value === true}
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" />}
                label="No"
                id="No"
                checked={value === false}
              />
            </RadioGroup>
          </FormControl>
          {value === true && (
            <FormControl component="fieldset">
              <FormLabel focused={false}>
                How much of the purchase price do you want to add in financed
                IR?
              </FormLabel>
              <PercentageField
                testId="financedIrPercentage"
                value={financedIrPercentage}
                onChange={(name, newValue) =>
                  loanEconomicsStore.setFinancedIrPercentage(
                    Number(newValue) || 0
                  )
                }
                variant="outlined"
                decimalScale={2}
                allowNegative={false}
                limitedPercentage
                min={0}
                inputClassname={classes.percentageField}
              />
            </FormControl>
          )}
        </Box>
      </Box>
    );
  }
);
